import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Box,
  Avatar,
  Badge,
  Spinner,
  Input,
  InputGroup,
  InputLeftElement,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
  useColorModeValue
} from "@chakra-ui/react";
import { FaSearch, FaUser, FaEnvelope, FaPhone } from "react-icons/fa";
import { useRef } from "react";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
  query,
  where,
} from "firebase/firestore";
import { db, auth } from "../firebase/firebase";
import { IconWrapper } from "./IconWrapper";
import { showToast } from "../utils/toastUtils";

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
  organizationId?: string; // Make optional since it might not be in members array
  dept?: string;
}

interface AddNewAdminModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentAdminId: string;
  organizationId: string;
}

const AddNewAdminModal: React.FC<AddNewAdminModalProps> = ({
  isOpen,
  onClose,
  currentAdminId,
  organizationId,
}) => {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isTransferring, setIsTransferring] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const cancelRef = useRef<any>(null);

  const dialogBg = useColorModeValue("zs.card", "zs.card");
  const dialogText = useColorModeValue("zs.text", "zs.text");
  const bodyText = useColorModeValue("zs.muted", "zs.muted");


  // Fetch users from the organization
  useEffect(() => {
    const fetchUsers = async () => {
      if (!isOpen) return;
      
      setIsLoading(true);
      try {
        // Get organization data to access members
        const orgRef = doc(db, "organization", organizationId);
        const orgDoc = await getDoc(orgRef);
        
        if (!orgDoc.exists()) {
          showToast({
            title: "Error",
            description: "Organization not found.",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
          return;
        }

        const orgData = orgDoc.data();
        const members = orgData.members || [];
        
        // Filter out the current admin and only show non-admin users
        const eligibleUsers = members.filter((member: any) => 
          member.id !== currentAdminId && member.role !== "admin"
        );

        setUsers(eligibleUsers);
        setFilteredUsers(eligibleUsers);

        if (eligibleUsers.length === 0) {
          showToast({
            title: "No Eligible Users",
            description: "There are no other users in your organization to transfer admin privileges to.",
            status: "warning",
            duration: 4000,
            isClosable: true,
          });
        }
      } catch (error) {
        console.error("Error fetching users:", error);
        showToast({
          title: "Error",
          description: "Failed to fetch users.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
  }, [isOpen, organizationId, currentAdminId]);

  // Filter users based on search query
  useEffect(() => {
    const filtered = users.filter(user => {
      const searchLower = searchQuery.toLowerCase();
      return (
        user.firstName.toLowerCase().includes(searchLower) ||
        user.lastName.toLowerCase().includes(searchLower) ||
        user.email.toLowerCase().includes(searchLower) ||
        user.role.toLowerCase().includes(searchLower)
      );
    });
    setFilteredUsers(filtered);
  }, [searchQuery, users]);

  const handleTransferAdmin = () => {
    if (!selectedUser) {
      showToast({
        title: "Error",
        description: "Please select a user to transfer admin privileges to.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setShowConfirmDialog(true);
  };

  const confirmTransferAdmin = async () => {
    if (!selectedUser) return;
    
    // Validate required data
    if (!currentAdminId) {
      showToast({
        title: "Error",
        description: "Current admin ID is missing",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!organizationId) {
      showToast({
        title: "Error",
        description: "Organization ID is missing",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (currentAdminId === selectedUser.id) {
      showToast({
        title: "Error",
        description: "Cannot transfer admin privileges to yourself",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Validate that we have all required user data
    if (!selectedUser.firstName || !selectedUser.lastName || !selectedUser.email || !selectedUser.phone) {
      showToast({
        title: "Error",
        description: "Selected user data is incomplete",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    setIsTransferring(true);
    setShowConfirmDialog(false);
    try {
   

      const batch = writeBatch(db);

      // 1. Remove current admin from admins collection

      const currentAdminRef = doc(db, "admins", currentAdminId);
      batch.delete(currentAdminRef);

      // 2. Add new user to admins collection
   
      const newAdminRef = doc(db, "admins", selectedUser.id);
      batch.set(newAdminRef, {
        id: selectedUser.id,
        firstName: selectedUser.firstName,
        lastName: selectedUser.lastName,
        email: selectedUser.email,
        phone: selectedUser.phone,
        role: "admin",
        dept: selectedUser.dept || null,
        organizationId: organizationId, // Use the organizationId from props instead of selectedUser.organizationId
      });

      // 3. Update organization document - change admin field and update member roles
     
      const orgRef = doc(db, "organization", organizationId);
      const orgDoc = await getDoc(orgRef);
      
      if (!orgDoc.exists()) {
        throw new Error("Organization document does not exist");
      }
      
      const orgData = orgDoc.data();

      
      if (!orgData || !orgData.members) {
        throw new Error("Invalid organization data - members array is missing");
      }

      // Update members array - change roles
      const updatedMembers = orgData.members.map((member: any) => {
        if (member.id === currentAdminId) {
          console.log("Demoting current admin:", member.id);
          return { ...member, role: "user" }; // Demote current admin
        }
        if (member.id === selectedUser.id) {
          console.log("Promoting selected user:", member.id);
          return { ...member, role: "admin" }; // Promote selected user
        }
        return member;
      });

    

      // Update organization with new admin and updated members
      batch.update(orgRef, {
        admin: selectedUser.id,
        members: updatedMembers,
      });

      // 4. Update users collection
      console.log("Step 4: Updating users collection");
      const currentUserRef = doc(db, "users", currentAdminId);
      batch.update(currentUserRef, { role: "user" });

      const newUserRef = doc(db, "users", selectedUser.id);
      batch.update(newUserRef, { role: "admin" });

      // 5. Update all users that have the old admin as their adminId
      console.log("Step 5: Updating users with old adminId");
      const usersQuery = query(
        collection(db, "users"),
        where("adminId", "==", currentAdminId)
      );
      const usersSnapshot = await getDocs(usersQuery);
      
      console.log("Users with old adminId:", usersSnapshot.docs.length);
      usersSnapshot.docs.forEach((userDoc) => {
        const userRef = doc(db, "users", userDoc.id);
        batch.update(userRef, { adminId: selectedUser.id });
      });

      // Commit all changes
      console.log("Committing batch...");
      await batch.commit();
      console.log("Batch committed successfully");

      showToast({
        title: "Success",
        description: `Admin privileges transferred to ${selectedUser.firstName} ${selectedUser.lastName}`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      onClose();
    } catch (error) {
      console.error("Error transferring admin privileges:", error);
      console.error("Error details:", {
        message: error instanceof Error ? error.message : "Unknown error",
        stack: error instanceof Error ? error.stack : undefined,
        currentAdminId,
        selectedUserId: selectedUser?.id,
        organizationId,
      });
      
      showToast({
        title: "Error",
        description: `Failed to transfer admin privileges: ${error instanceof Error ? error.message : "Unknown error"}`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsTransferring(false);
    }
  };

  const handleClose = () => {
    setSelectedUser(null);
    setSearchQuery("");
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="xl"
      isCentered
      motionPreset="none"
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent bg="zs.card" borderWidth="1px" borderColor="zs.border">
        <ModalHeader color="zs.accent">Transfer Admin Privileges</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text color="zs.muted" fontSize="sm">
              Select a user from your organization to transfer admin privileges to. 
              This will demote the current admin to a regular user.
            </Text>

            {/* Search Bar */}
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <IconWrapper icon={FaSearch} className="text-gray-400" size={16} />
              </InputLeftElement>
              <Input
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                bg="zs.card"
                borderColor="zs.border"
                color="zs.text"
                _placeholder={{ color: "zs.muted" }}
                _focus={{
                  borderColor: "zs.accent",
                  boxShadow: "0 0 0 1px var(--chakra-colors-zs-accent)",
                }}
              />
            </InputGroup>

            {/* Users List */}
            <Box maxH="400px" overflowY="auto" borderWidth="1px" borderRadius="md" p={2} borderColor="zs.border" bg="zs.cardMuted">
              {isLoading ? (
                <VStack spacing={4} py={8}>
                  <Spinner size="lg" color="zs.accent" />
                  <Text color="zs.muted">Loading users...</Text>
                </VStack>
              ) : filteredUsers.length === 0 ? (
                <VStack spacing={4} py={8}>
                  <Text color="zs.muted">
                    {searchQuery ? "No users found matching your search." : "No eligible users found."}
                  </Text>
                </VStack>
              ) : (
                <VStack spacing={2} align="stretch">
                  {filteredUsers.map((user) => (
                    <Box
                      key={user.id}
                      p={3}
                      borderWidth="1px"
                      borderRadius="md"
                      cursor="pointer"
                      transition="all 0.2s"
                      bg={selectedUser?.id === user.id ? "zs.card" : "zs.card"}
                      borderColor={selectedUser?.id === user.id ? "zs.accent" : "zs.border"}
                      _hover={{
                        bg: "zs.cardMuted",
                        borderColor: "zs.accent",
                      }}
                      onClick={() => setSelectedUser(user)}
                    >
                      <HStack spacing={3} align="start">
                        <Avatar
                          size="sm"
                          name={`${user.firstName} ${user.lastName}`}
                          bg="zs.accent"
                          color="white"
                        />
                        <VStack align="start" spacing={1} flex={1}>
                          <HStack justify="space-between" w="full">
                            <Text fontWeight="semibold" color="zs.text">
                              {user.firstName} {user.lastName}
                            </Text>
                            <Badge bgGradient="linear(to-r, #FBD282, #B59143)" color="white" variant="solid">
                              {user.role}
                            </Badge>
                          </HStack>
                          <HStack spacing={4} fontSize="sm" color="zs.muted">
                             <HStack spacing={1}>
                               <IconWrapper icon={FaEnvelope} size={12} />
                               <Text>{user.email}</Text>
                             </HStack>
                             <HStack spacing={1}>
                               <IconWrapper icon={FaPhone} size={12} />
                               <Text>{user.phone}</Text>
                             </HStack>
                           </HStack>
                        </VStack>
                      </HStack>
                    </Box>
                  ))}
                </VStack>
              )}
            </Box>

            {selectedUser && (
              <Box p={4} bg="zs.cardMuted" borderRadius="md" borderWidth="1px" borderColor="zs.border">
                <Text fontWeight="semibold" color="zs.accent" mb={2}>
                  Selected User:
                </Text>
                <Text color="zs.text">
                  {selectedUser.firstName} {selectedUser.lastName} ({selectedUser.email})
                </Text>
                <Text fontSize="sm" color="zs.muted" mt={1}>
                  This user will become the new admin and you will be demoted to a regular user.
                </Text>
              </Box>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="gold"
            onClick={handleTransferAdmin}
            isLoading={isTransferring}
            loadingText="Transferring..."
            isDisabled={!selectedUser || isTransferring}
          >
            Transfer Admin Privileges
          </Button>
        </ModalFooter>

        {/* Confirmation Dialog */}
        <AlertDialog
          isOpen={showConfirmDialog}
          onClose={() => setShowConfirmDialog(false)}
          leastDestructiveRef={cancelRef}
        >
          <AlertDialogOverlay>
            <AlertDialogContent bg={dialogBg} color={dialogText} borderWidth="1px" borderColor="zs.border">
              <AlertDialogHeader fontSize="lg" fontWeight="bold" color={dialogText}>
                Confirm Admin Transfer
              </AlertDialogHeader>

              <AlertDialogBody color={bodyText}>
                Are you sure you want to transfer admin privileges to{" "}
                <strong>{selectedUser?.firstName} {selectedUser?.lastName}</strong>?
                <br /><br />
                This action will:
                <ul style={{ marginLeft: "20px", marginTop: "8px" }}>
                  <li>• Promote {selectedUser?.firstName} {selectedUser?.lastName} to admin</li>
                  <li>• Demote you to a regular user</li>
                  <li>• Update all related database records</li>
                </ul>
                <br />
                This action cannot be undone.
              </AlertDialogBody>

              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={() => setShowConfirmDialog(false)} variant="ghost">
                   Cancel
                 </Button>
                <Button
                  variant="gold"
                  onClick={confirmTransferAdmin}
                  ml={3}
                  isLoading={isTransferring}
                  loadingText="Transferring..."
                >
                  Confirm Transfer
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </ModalContent>
    </Modal>
  );
};

export default AddNewAdminModal;
